"use client";

import { Smile } from "lucide-react";
import type { CompactEmoji } from "emojibase";
import { fetchEmojis } from "emojibase";

import { Button } from "@/components/new/button";
import { Input } from "@/components/new/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/new/tabs";
import { useState, useEffect, useMemo } from "react";

interface EmojiButtonProps {
	onEmojiSelect: (emoji: string) => void;
}

export function EmojiButton({ onEmojiSelect }: EmojiButtonProps) {
	const [open, setOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [emojis, setEmojis] = useState<CompactEmoji[]>([]);
	const [recentEmojis, setRecentEmojis] = useState<CompactEmoji[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const loadEmojis = async () => {
			setIsLoading(true);
			try {
				const compactEmojis = await fetchEmojis("en", { compact: true });
				setEmojis(compactEmojis);

				// Load recent emojis from localStorage
				const localEmojis = localStorage.getItem("emojis");
				if (localEmojis) {
					setRecentEmojis(JSON.parse(localEmojis));
				}
			} catch (error) {
				console.error("Failed to load emojis:", error);
			} finally {
				setIsLoading(false);
			}
		};

		loadEmojis();
	}, []);

	const handleEmojiSelect = (emoji: CompactEmoji) => {
		onEmojiSelect(emoji.unicode);

		// Update recent emojis
		const updatedRecents = [
			emoji,
			...recentEmojis.filter((e) => e.unicode !== emoji.unicode),
		].slice(0, 10);

		setRecentEmojis(updatedRecents);

		localStorage.setItem("emojis", JSON.stringify(updatedRecents));

		setOpen(false);
	};

	const filteredEmojis = useMemo(() => {
		if (!searchQuery) return emojis; // Show All emojis if no search query

		return emojis.filter((emoji: CompactEmoji) => {
			if (emoji.label.includes(searchQuery.toLowerCase())) {
				return true;
			} else if (emoji.tags) {
				return emoji.tags.some((tag) => tag.includes(searchQuery.toLowerCase()));
			}
			return false;
		});
	}, [emojis, searchQuery]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
					<Smile className="h-4 w-4" />
					<span className="sr-only">Insert emoji</span>
				</Button>
			</PopoverTrigger>

			<PopoverContent className="w-80 p-0" align="start">
				<div className="border-b p-3">
					<Input
						placeholder="Search emojis..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="h-8"
					/>
				</div>

				<Tabs defaultValue="search" className="w-full">
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger value="search">Search</TabsTrigger>
						<TabsTrigger value="recent">Recent</TabsTrigger>
					</TabsList>

					<TabsContent value="search" className="p-0">
						{isLoading ? (
							<div className="flex h-48 items-center justify-center">
								<p className="text-sm text-muted-foreground">Loading emojis...</p>
							</div>
						) : filteredEmojis.length > 0 ? (
							<div className="grid max-h-[300px] grid-cols-8 gap-2 overflow-y-auto p-3">
								{filteredEmojis.map((emoji) => (
									<button
										type="button"
										key={emoji.unicode}
										onClick={() => handleEmojiSelect(emoji)}
										className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-muted"
										title={emoji.label}
									>
										<span className="text-lg">{emoji.unicode}</span>
									</button>
								))}
							</div>
						) : (
							<div className="flex h-48 items-center justify-center">
								<p className="text-sm text-muted-foreground">No emojis found</p>
							</div>
						)}
					</TabsContent>
					<TabsContent value="recent" className="p-0">
						{recentEmojis.length > 0 ? (
							<div className="grid max-h-[300px] grid-cols-8 gap-1 overflow-y-auto p-3">
								{recentEmojis.map((emoji) => (
									<button
										key={emoji.hexcode}
										onClick={() => handleEmojiSelect(emoji)}
										className="flex h-8 w-8 items-center justify-center rounded transition-colors hover:bg-muted"
										title={emoji.label}
									>
										<span className="text-lg">{emoji.unicode}</span>
									</button>
								))}
							</div>
						) : (
							<div className="flex h-48 items-center justify-center">
								<p className="text-sm text-muted-foreground">No recent emojis</p>
							</div>
						)}
					</TabsContent>
				</Tabs>
			</PopoverContent>
		</Popover>
	);
}
